import React, { useMemo, useState } from "react";
import { formatComma } from "../../decision/importColumns.mjs";

// Показ розрахунку моделі (AHP + TOPSIS), як у табл. 3, 5 і 6 статті.
// Компонент НІЧОГО не рахує: усі числа (ваги, r, v, еталони, d+, d-, C, ранги)
// приходять у відповіді DSS (model, queue[].details/dPlus/dMinus/score) і лише
// форматуються з десятковою комою. Кроки "підставлених чисел" -- це та сама
// формула з числами у дужках, значення самих кроків теж беруться з відповіді.

// Позначення критеріїв зі статті (табл. 1).
const SYMBOLS = { ses: "s", dorm: "g", act: "f", zone: "q" };
const symbol = (code) => SYMBOLS[code] || code;

const num4 = (value) => formatComma(value, 4);
// Вхідні значення і межі: до двох знаків, без зайвих нулів (48,6; 25; 24,5).
const plain = (value) =>
  typeof value === "number" && Number.isFinite(value) ? String(Number(value.toFixed(2))).replace(".", ",") : "—";

function formatCr(cr) {
  if (typeof cr !== "number") return "—";
  return Math.abs(cr) < 1e-9 ? "0" : formatComma(cr, 4);
}

function ruleLabel(rank) {
  return rank === null || rank === undefined ? "—" : rank;
}

// Підпис значення: "27 → 25" для обрізаного понад шкалу, інакше саме значення.
function ValueWithClamp({ value, event }) {
  if (!event) return <>{plain(value)}</>;
  return (
    <span title={`у файлі ${plain(event.original)}, взято ${plain(event.value)}`} data-testid="clamped-note">
      {plain(event.original)} → {plain(event.value)} (обрізано)
    </span>
  );
}

const ModelCalculationPanel = ({ model, rows, clamped, normalization, defaultOpen = false }) => {
  const criteria = model?.criteria || [];
  const detailedRows = useMemo(
    () => [...(rows || [])].filter((r) => r.details).sort((a, b) => a.id - b.id),
    [rows]
  );
  const clampedByKey = useMemo(
    () => new Map((clamped || []).map((e) => [`${e.studentId}:${e.criterionCode}`, e])),
    [clamped]
  );
  const [selectedId, setSelectedId] = useState(null);

  if (!model || criteria.length === 0) return null;
  if (detailedRows.length === 0) {
    return (
      <div className="input-component" data-testid="model-calculation-panel">
        <h3 className="text-header">Розрахунок моделі</h3>
        <p className="text-header">
          Проміжні величини розрахунку недоступні: DSS-модуль не повернув details (можливо, працює деградований
          резерв).
        </p>
      </div>
    );
  }

  const selected = detailedRows.find((r) => r.id === selectedId) || detailedRows[0];
  const eventFor = (row, code) => clampedByKey.get(`${row.id}:${code}`);
  const sequence = (row, pick) => criteria.map((c) => pick(c, row.details));

  return (
    <details className="input-component" open={defaultOpen} data-testid="model-calculation-panel">
      <summary className="text-header">
        <strong>Розрахунок моделі</strong> ({normalization === "vector" ? "класичний режим" : "абсолютна нормалізація"})
      </summary>

      <h3 className="text-header">Ваги AHP</h3>
      <table className="table" aria-label="Ваги AHP">
        <thead>
          <tr>
            <th className="table-header">Критерій</th>
            <th className="table-header">Межі шкали</th>
            <th className="table-header">Вага w</th>
          </tr>
        </thead>
        <tbody>
          {criteria.map((c) => (
            <tr key={c.code}>
              <td className="table-content">{c.title || c.code}</td>
              <td className="table-content">
                {c.min === null || c.max === null ? "—" : `${plain(c.min)}–${plain(c.max)}`}
              </td>
              <td className="table-content">{num4(c.weight)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-header">
        Відношення узгодженості CR = <strong>{formatCr(model.cr)}</strong>
        {Math.abs(model.cr) < 1e-9 ? " (судження ідеально узгоджені)" : ""}.
      </p>

      <h3 className="text-header">Нормалізовані та зважені значення</h3>
      <table className="table" aria-label="Нормалізовані та зважені значення">
        <thead>
          <tr>
            <th className="table-header">Здобувач</th>
            {criteria.map((c) => (
              <th key={`r-${c.code}`} className="table-header">{`r(${symbol(c.code)})`}</th>
            ))}
            {criteria.map((c) => (
              <th key={`v-${c.code}`} className="table-header">{`v(${symbol(c.code)})`}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {detailedRows.map((row) => (
            <tr key={row.id}>
              <td className="table-content">{row.pib || `#${row.id}`}</td>
              {criteria.map((c) => {
                const event = eventFor(row, c.code);
                return (
                  <td
                    key={`r-${c.code}`}
                    className="table-content"
                    title={event ? `у файлі ${plain(event.original)}, взято ${plain(event.value)}` : undefined}
                  >
                    {num4(row.details.normalized[c.code])}
                    {event ? " †" : ""}
                  </td>
                );
              })}
              {criteria.map((c) => (
                <td key={`v-${c.code}`} className="table-content">
                  {num4(row.details.weighted[c.code])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {(clamped || []).length > 0 && (
        <p className="text-header">† значення понад максимум шкали обрізано до максимуму (наведіть курсор).</p>
      )}

      <h3 className="text-header">Відстані до еталонів</h3>
      <table className="table" aria-label="Відстані до еталонів">
        <thead>
          <tr>
            <th className="table-header">Здобувач</th>
            <th className="table-header">d⁺</th>
            <th className="table-header">d⁻</th>
            <th className="table-header">C</th>
            <th className="table-header">Ранг за R</th>
            <th className="table-header">Ранг за C</th>
            <th className="table-header">Місце в черзі</th>
          </tr>
        </thead>
        <tbody>
          {detailedRows.map((row) => (
            <tr key={row.id}>
              <td className="table-content">{row.pib || `#${row.id}`}</td>
              <td className="table-content">{num4(row.dPlus)}</td>
              <td className="table-content">{num4(row.dMinus)}</td>
              <td className="table-content">{num4(row.score)}</td>
              <td className="table-content">{ruleLabel(row.sumRank)}</td>
              <td className="table-content">{ruleLabel(row.scoreRank)}</td>
              <td className="table-content">
                {ruleLabel(row.modelRank)}
                {typeof row.privilege === "number" && row.privilege > 1 ? " (пільга)" : ""}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <h3 className="text-header">Покроковий розрахунок</h3>
      <div className="input-block text-header">
        <select
          className="header-content-select"
          aria-label="Здобувач для покрокового розрахунку"
          value={selected.id}
          onChange={(e) => setSelectedId(Number(e.target.value))}
        >
          {detailedRows.map((row) => (
            <option key={row.id} value={row.id}>
              {row.pib || `#${row.id}`}
            </option>
          ))}
        </select>
      </div>

      <div data-testid="calculation-steps">
        <p className="text-header">
          <strong>Нормалізація (формула 4):</strong> r = (x − min)/(max − min)
        </p>
        {criteria.map((c) => {
          const event = eventFor(selected, c.code);
          const x = selected.details.raw[c.code];
          return (
            <p className="text-header" key={`step-r-${c.code}`}>
              r<sub>{symbol(c.code)}</sub> = ({plain(x)} − {plain(c.min)})/({plain(c.max)} − {plain(c.min)}) ={" "}
              {num4(selected.details.normalized[c.code])}
              {event && (
                <>
                  {" "}
                  [x: <ValueWithClamp value={x} event={event} />]
                </>
              )}
            </p>
          );
        })}

        <p className="text-header">
          <strong>Зважування (формула 5):</strong> v = w · r
        </p>
        {criteria.map((c) => (
          <p className="text-header" key={`step-v-${c.code}`}>
            v<sub>{symbol(c.code)}</sub> = {num4(c.weight)} · {num4(selected.details.normalized[c.code])} ={" "}
            {num4(selected.details.weighted[c.code])}
          </p>
        ))}

        <p className="text-header">
          <strong>Відстані до еталонів (формули 6–7):</strong>
        </p>
        <p className="text-header">
          d⁺ = √({sequence(selected, (c, d) => `(${num4(d.weighted[c.code])} − ${num4(d.ideal[c.code])})²`).join(" + ")}) ={" "}
          {num4(selected.dPlus)}
        </p>
        <p className="text-header">
          d⁻ = √({sequence(selected, (c, d) => `(${num4(d.weighted[c.code])} − ${num4(d.antiIdeal[c.code])})²`).join(" + ")}) ={" "}
          {num4(selected.dMinus)}
        </p>

        <p className="text-header">
          <strong>Близькість до ідеалу (формула 8):</strong>
        </p>
        <p className="text-header" data-testid="step-score">
          C = d⁻/(d⁺ + d⁻) = {num4(selected.dMinus)}/({num4(selected.dPlus)} + {num4(selected.dMinus)}) ={" "}
          <strong>{num4(selected.score)}</strong>
        </p>
      </div>
    </details>
  );
};

export default ModelCalculationPanel;
